import React, { FC, ReactElement } from 'react';
import Head from 'next/head';

interface Img {
  height?: number;
  url: string;
  width?: number;
}

export interface SocialMetadataOverrides {
  currency?: string;
  overrideDescription?: string;
  overrideImages?: Img[];
  overrideTitle?: string;
  overrideType?: string;
  price?: number;
}

export interface SeoProps {
  canonicalUrl: string;
  seoDescription: string;
  seoTitle: string;
}

type SocialMetadataProps = SocialMetadataOverrides & SeoProps;

const logoImg: Img = {
  height: 82,
  url: 'https://d3nn9jcidi1lkh.cloudfront.net/mkt/logo_small.png',
  width: 219,
};

const SocialMetadata: FC<SocialMetadataProps> = ({
  canonicalUrl,
  currency,
  overrideDescription,
  overrideImages,
  overrideTitle,
  overrideType,
  price,
  seoDescription,
  seoTitle,
}) => {
  const defaultValues = {
    description: seoDescription,
    images: [logoImg],
    title: seoTitle,
    type: 'website',
  };

  const d = overrideDescription || defaultValues.description;
  const t = overrideTitle || defaultValues.title;
  const type = overrideType || defaultValues.type;

  return (
    <Head>
      {canonicalUrl && <meta content={canonicalUrl} property="og:url" />}
      <meta content={type} property="og:type" />
      <meta content={t} property="og:title" />
      <meta content={t} itemProp="name" />
      <meta content={d} property="og:description" />
      <meta content={d} itemProp="description" />
      {price && currency && (
        <>
          <meta content={price.toString()} property="product:price:amount" />
          <meta content={currency} property="product:price:currency" />
        </>
      )}
      {(overrideImages || defaultValues.images).map(el => (
        <>
          <meta content={el.url} property="og:image" />
          <meta content={el.url} itemProp="image" />
          {el.width && <meta content={el.width.toString()} property="og:image:width" />}
          {el.height && <meta content={el.height.toString()} property="og:image:height" />}
        </>
      ))}
    </Head>
  );
};

export const SocialMetadataFactory = (overrides: SocialMetadataOverrides) => (seoProps: SeoProps): ReactElement => (
  <SocialMetadata {...seoProps} {...overrides} />
);
